import React, { useState } from "react";
import { X, Trash2, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";
import { List, Tag, Task } from "../../types";
import { ProposedTodo } from "../../hooks/useVoiceToTodo";
import RichTextEditor from "../forms/LazyRichTextEditor";

const PRIORITY_LABELS: Record<number, { label: string; color: string }> = {
  1: { label: "High", color: "text-red-500" },
  2: { label: "Medium", color: "text-orange-500" },
  3: { label: "Low", color: "text-yellow-500" },
};

export interface TodoCardProps {
  todo: ProposedTodo;
  lists: List[];
  tags: Tag[];
  onUpdate: (todo: ProposedTodo) => void;
  onRemove: () => void;
  /** When false, details stay open and the expand/collapse control is hidden. */
  collapsible?: boolean;
  /**
   * When set, the card can become a sub-task of one of these tasks.
   * Email review leaves this out, because those cards are already sub-tasks.
   */
  attachableTasks?: Task[];
  onChooseParent?: () => void;
  /**
   * When false, the card has no list picker. Email review sets this, because
   * a sub-task always uses its parent task's list.
   */
  showList?: boolean;
}

export const TodoCard: React.FC<TodoCardProps> = ({
  todo,
  lists,
  tags,
  onUpdate,
  onRemove,
  collapsible = true,
  attachableTasks,
  onChooseParent,
  showList = true,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isExpanded = !collapsible || expanded;

  const list = lists.find((l) => l.id === todo.list_id);
  const todoTags = tags.filter((t) => todo.tag_ids.includes(t.id));
  const parentTask =
    attachableTasks?.find((task) => task.id === todo.parent_task_id) ?? null;
  const parentList = parentTask
    ? lists.find((item) => item.id === parentTask.list_id) ?? null
    : null;
  const canAttach = Boolean(attachableTasks && onChooseParent);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const choiceClass = (selected: boolean) =>
    `rounded-soft px-3 py-1 text-sm font-medium ${
      selected
        ? "bg-warm-blue text-white"
        : "text-warm-text-secondary hover:text-warm-text-primary"
    }`;

  return (
    <div>
      {canAttach && (
        <div className="mb-2 inline-flex max-w-full rounded-soft border border-warm-border bg-warm-bg-card p-0.5">
          <button
            type="button"
            aria-pressed={!todo.parent_task_id}
            onClick={() => onUpdate({ ...todo, parent_task_id: null })}
            className={choiceClass(!todo.parent_task_id)}
          >
            New task
          </button>
          <button
            type="button"
            aria-pressed={Boolean(todo.parent_task_id)}
            onClick={onChooseParent}
            className={`${choiceClass(Boolean(todo.parent_task_id))} max-w-[16rem] truncate`}
          >
            {parentTask ? `Sub-task of ${parentTask.title}` : "Sub-task of…"}
          </button>
        </div>
      )}
      <div className="bg-warm-bg-elevated border border-warm-border rounded-lg p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <input
            type="text"
            aria-label="Task title"
            value={todo.title}
            onChange={(e) => onUpdate({ ...todo, title: e.target.value })}
            className="w-full bg-transparent text-warm-text-primary font-medium focus:outline-none focus:ring-1 focus:ring-warm-blue rounded px-1 -ml-1"
          />
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {list && !parentTask && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-warm-bg-elevated rounded text-xs text-warm-text-secondary">
                {list.emoji} {list.name}
              </span>
            )}
            <span
              className={`text-xs font-medium ${PRIORITY_LABELS[todo.priority]?.color || "text-warm-text-tertiary"}`}
            >
              {PRIORITY_LABELS[todo.priority]?.label || "Low"} Priority
            </span>
            {todo.scheduled_date && (
              <span className="text-xs text-warm-blue">
                {formatDate(todo.scheduled_date)}
              </span>
            )}
            {todoTags.map((tag) => (
              <span
                key={tag.id}
                className="px-2 py-0.5 rounded text-xs text-white"
                style={{ backgroundColor: tag.color }}
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {collapsible && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              aria-label={
                isExpanded ? `Collapse ${todo.title}` : `Expand ${todo.title}`
              }
              className="p-1.5 text-warm-text-tertiary hover:text-warm-text-secondary rounded transition-colors"
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          )}
          <button
            onClick={onRemove}
            aria-label="Remove"
            className="p-1.5 text-warm-text-tertiary hover:text-red-500 rounded transition-colors"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4 space-y-3 border-t border-warm-border pt-4">
          <div>
            <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
              Description
            </label>
            <RichTextEditor
              content={todo.description}
              onChange={(content) => onUpdate({ ...todo, description: content })}
              placeholder="Add a description..."
            />
          </div>

          {parentTask ? (
            <p className="text-sm text-warm-text-secondary">
              {parentList
                ? `You cannot pick a list. This sub-task uses ${parentTask.title}'s list, ${parentList.name}.`
                : `You cannot pick a list. ${parentTask.title} has no list, so this sub-task has no list.`}
            </p>
          ) : showList ? (
            <div>
              <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
                List
              </label>
              <div className="flex flex-wrap gap-2">
                {lists.map((l) => (
                  <button
                    key={l.id}
                    onClick={() =>
                      onUpdate({
                        ...todo,
                        list_id: todo.list_id === l.id ? null : l.id,
                      })
                    }
                    className={`px-3 py-1.5 rounded-soft text-sm transition-all duration-150 ease-in-out ${
                      todo.list_id === l.id
                        ? "bg-warm-blue text-white shadow-soft-sm"
                        : "bg-warm-bg-elevated border border-warm-border text-warm-text-primary hover:border-warm-blue/50"
                    }`}
                  >
                    <span className="mr-1.5">{l.emoji}</span>
                    {l.name}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div>
            <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
              Priority
            </label>
            <div className="flex gap-2">
              {[
                { value: 1, label: "High", color: "border-l-red-700" },
                { value: 2, label: "Medium", color: "border-l-orange-500" },
                { value: 3, label: "Low", color: "border-l-yellow-400" },
              ].map((p) => (
                <button
                  key={p.value}
                  onClick={() => onUpdate({ ...todo, priority: p.value })}
                  className={`flex-1 px-3 py-1.5 rounded-soft border-l-4 text-sm transition-all duration-150 ease-in-out text-left ${
                    todo.priority === p.value
                      ? `${p.color} bg-warm-blue/10 border border-warm-border font-medium text-warm-text-primary`
                      : `${p.color} bg-warm-bg-elevated border border-warm-border hover:bg-warm-border-subtle text-warm-text-secondary`
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
              Tags
            </label>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => {
                const isSelected = todo.tag_ids.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    onClick={() => {
                      const newTagIds = isSelected
                        ? todo.tag_ids.filter((id) => id !== tag.id)
                        : [...todo.tag_ids, tag.id];
                      onUpdate({ ...todo, tag_ids: newTagIds });
                    }}
                    aria-pressed={isSelected}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      isSelected
                        ? "ring-2 ring-warm-text-primary ring-offset-2 ring-offset-warm-bg-card"
                        : "opacity-60 hover:opacity-100"
                    }`}
                    style={{ backgroundColor: tag.color, color: "white" }}
                  >
                    {tag.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-warm-text-tertiary mb-1">
                Scheduled Date
              </label>
              <input
                type="date"
                value={todo.scheduled_date || ""}
                onChange={(e) =>
                  onUpdate({
                    ...todo,
                    scheduled_date: e.target.value || null,
                  })
                }
                className="w-full bg-warm-bg-elevated border border-warm-border rounded px-2 py-1 text-sm text-warm-text-primary focus:outline-none focus:ring-1 focus:ring-warm-blue"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-warm-text-tertiary mb-1">
                Deadline
              </label>
              <input
                type="date"
                value={todo.deadline_date || ""}
                onChange={(e) =>
                  onUpdate({
                    ...todo,
                    deadline_date: e.target.value || null,
                  })
                }
                className="w-full bg-warm-bg-elevated border border-warm-border rounded px-2 py-1 text-sm text-warm-text-primary focus:outline-none focus:ring-1 focus:ring-warm-blue"
              />
            </div>
          </div>

          {todo.subtasks.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-warm-text-tertiary mb-1">
                Subtasks
              </label>
              <div className="space-y-1">
                {todo.subtasks.map((subtask, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={subtask.title}
                      onChange={(e) => {
                        const newSubtasks = [...todo.subtasks];
                        newSubtasks[index] = { title: e.target.value };
                        onUpdate({ ...todo, subtasks: newSubtasks });
                      }}
                      className="flex-1 bg-warm-bg-elevated border border-warm-border rounded px-2 py-1 text-sm text-warm-text-primary focus:outline-none focus:ring-1 focus:ring-warm-blue"
                    />
                    <button
                      onClick={() => {
                        const newSubtasks = todo.subtasks.filter(
                          (_, i) => i !== index,
                        );
                        onUpdate({ ...todo, subtasks: newSubtasks });
                      }}
                      className="p-1 text-warm-text-tertiary hover:text-red-500"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {todo.confidence < 0.7 && (
            <div className="flex items-center gap-2 text-xs text-orange-500">
              <AlertCircle size={14} />
              <span>Low confidence extraction - please verify the details</span>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};
